import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, ArrowUp, ChevronDown, Lock } from 'lucide-react';
import { cn, MarkdownMath } from '@jeraisonne/ui';
import {
  BLOG_LEVEL_LABELS,
  activeGatedPollIndex,
  type BlogBlock,
  type BlogBlockEvent,
  type BlogPollCount,
  type BlogSocialLink,
} from '@jeraisonne/db';
import {
  emitBlogVoteRecorded,
  fetchMyBlogPollVotesApp,
  incrementBlogView,
  recordBlogBlockEvent,
  recordBlogPollVoteApp,
} from '@/lib/blog/blog-client';
import { BlogPoll } from './blog-poll';
import { BlogCompletionReward } from './blog-completion-reward';

interface ReflectionJourneyProps {
  slug: string;
  articleId: string;
  /** Titre de l'article — affiché dans la carte récompense de fin de parcours. */
  articleTitle: string;
  blocks: BlogBlock[];
  conclusionMd: string;
  /** Réseaux où réagir/partager — pour la carte récompense de fin. */
  socialLinks: BlogSocialLink[];
}

/**
 * Porté depuis apps/marketing/src/components/reflection-journey.tsx —
 * mécanique de révélation des blocs et de lecture à 3 niveaux conservée à
 * l'identique. La mécanique newsletter (abonné confirmé requis pour voter,
 * `NewsletterSignup`, jeton `vote_token`, polling cross-device) est
 * ENTIÈREMENT retirée : l'utilisateur app est toujours authentifié, le vote
 * part donc immédiatement (`recordBlogPollVoteApp`, auth.uid()). Pas de
 * persistance `localStorage` du parcours non plus — la BDD (via `auth.uid()`)
 * est déjà la source de vérité cross-device, contrairement au lecteur anonyme
 * marketing (cf. `fetchMyBlogPollVotesApp` au montage).
 */
export function ReflectionJourney({
  slug,
  articleId,
  articleTitle,
  blocks,
  conclusionMd,
  socialLinks,
}: ReflectionJourneyProps) {
  const [votes, setVotes] = useState<Record<number, number>>({});
  const [counts, setCounts] = useState<Record<number, BlogPollCount[]>>({});
  // Envois en cours, PAR bloc : un vote lent sur un sondage ne doit jamais
  // empêcher de voter le sondage suivant.
  const [pendingBlocks, setPendingBlocks] = useState<Set<number>>(() => new Set());
  const [voteError, setVoteError] = useState<number | null>(null);
  // Blocs « lus sans voter » — en mémoire seulement (pas d'équivalent serveur,
  // réinitialisé au remount : compromis UX acceptable).
  const [skipped, setSkipped] = useState<Record<number, boolean>>({});

  // Analytics « lecture à profondeur variable » : un événement par bloc/session.
  const firedRef = useRef<Set<string>>(new Set());
  const fire = useCallback(
    (blockIndex: number, event: BlogBlockEvent) => {
      const key = `${blockIndex}:${event}`;
      if (firedRef.current.has(key)) return;
      firedRef.current.add(key);
      void recordBlogBlockEvent(articleId, blockIndex, event);
    },
    [articleId],
  );

  // Version « article complet » (`?full=1`) : tous les blocs dévoilés, tous
  // les sondages en lecture seule (résultats visibles, aucun clic).
  const [revealAll, setRevealAll] = useState(false);
  useEffect(() => {
    try {
      const param = new URLSearchParams(window.location.search).get('full');
      if (param !== null && param !== '0' && param !== 'false') setRevealAll(true);
    } catch {
      /* URL indisponible */
    }
  }, []);

  // Réconciliation avec la BDD au montage : restaure les réponses de
  // l'utilisateur (auth.uid()) + la distribution agrégée fraîche. Incrémente
  // aussi la vue (une fois par onglet, sessionStorage).
  useEffect(() => {
    let cancelled = false;
    void fetchMyBlogPollVotesApp(articleId).then(({ votes: myVotes, counts: myCounts }) => {
      if (cancelled) return;
      setCounts(myCounts);
      if (myVotes.length > 0) {
        setVotes((prev) => {
          const next = { ...prev };
          for (const v of myVotes) next[v.block_index] = v.choice_index;
          return next;
        });
      }
    });
    try {
      const viewKey = `blog_viewed_${slug}`;
      if (!sessionStorage.getItem(viewKey)) {
        sessionStorage.setItem(viewKey, '1');
        void incrementBlogView(articleId);
      }
    } catch {
      /* best-effort */
    }
    return () => {
      cancelled = true;
    };
  }, [slug, articleId]);

  /**
   * Un bloc est révélé si le sondage du bloc PRÉCÉDENT a reçu une réponse
   * (ou si le bloc précédent n'avait pas de sondage). Le bloc 0 l'est toujours.
   */
  const revealed = useMemo(() => {
    if (revealAll) return blocks.map(() => true);
    const out: boolean[] = [];
    for (let i = 0; i < blocks.length; i++) {
      if (i === 0) {
        out[i] = true;
        continue;
      }
      const prev = blocks[i - 1]!;
      out[i] = prev.poll
        ? votes[i - 1] !== undefined || skipped[i - 1] === true
        : (out[i - 1] ?? false);
    }
    return out;
  }, [blocks, votes, skipped, revealAll]);

  const allRevealed = revealed.every(Boolean);
  const firstLockedIndex = revealed.findIndex((r) => !r);
  const activeGateIndex = useMemo(
    () =>
      activeGatedPollIndex(
        blocks,
        revealed,
        blocks.map((_, i) => votes[i] !== undefined || skipped[i] === true),
      ),
    [blocks, revealed, votes, skipped],
  );

  useEffect(() => {
    if (revealAll) return;
    revealed.forEach((r, i) => {
      if (r) fire(i, 'level1_view');
    });
  }, [revealed, fire, revealAll]);

  const submitVote = useCallback(
    async (blockIndex: number, choiceIndex: number) => {
      setPendingBlocks((s) => new Set(s).add(blockIndex));
      setVoteError((e) => (e === blockIndex ? null : e));
      try {
        const { counts: freshCounts } = await recordBlogPollVoteApp(
          articleId,
          blockIndex,
          choiceIndex,
        );
        setCounts((c) => ({ ...c, [blockIndex]: freshCounts ?? [] }));
        setVotes((v) => ({ ...v, [blockIndex]: choiceIndex }));
        // Le badge « raisonneurs » se recompte : peut-être le 1er vote de ce
        // lecteur sur cet article → +1 compte distinct.
        emitBlogVoteRecorded();
      } catch (err) {
        setVoteError(blockIndex);
        throw err;
      } finally {
        setPendingBlocks((s) => {
          if (!s.has(blockIndex)) return s;
          const n = new Set(s);
          n.delete(blockIndex);
          return n;
        });
      }
    },
    [articleId],
  );

  async function handleVote(blockIndex: number, choiceIndex: number) {
    // Un sondage déjà répondu est verrouillé. On bloque seulement CE bloc s'il
    // a un envoi en cours (anti double-clic).
    if (votes[blockIndex] !== undefined || pendingBlocks.has(blockIndex)) return;
    try {
      await submitVote(blockIndex, choiceIndex);
    } catch {
      // L'erreur est déjà surfacée (voteError) → réessai possible.
    }
  }

  return (
    <div className="flex flex-col">
      {blocks.map((block, i) => {
        const isRevealed = revealed[i] ?? false;
        const isLast = i === blocks.length - 1;
        return (
          <div key={i}>
            {!isRevealed && i === firstLockedIndex && (
              <div className="border-primary-200 bg-primary-50 my-5 rounded-2xl border-2 px-5 py-5 text-center shadow-sm">
                <ArrowUp
                  className="text-primary-500 mx-auto mb-2 size-5 animate-bounce"
                  aria-hidden
                />
                <span className="bg-primary-100 text-primary-700 mb-2 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide">
                  <Lock className="size-3.5 shrink-0" aria-hidden />
                  Étape {firstLockedIndex} / {blocks.length}
                </span>
                <p className="text-sm font-bold text-gray-900">
                  Réponds au sondage ci-dessus pour dévoiler la suite
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  ou continue la lecture sans voter, juste au-dessus
                </p>
              </div>
            )}

            <section
              aria-hidden={!isRevealed}
              className={cn(
                'transition-all',
                isRevealed
                  ? 'mt-8 max-h-none opacity-100 duration-700'
                  : 'pointer-events-none max-h-0 select-none overflow-hidden opacity-0',
              )}
            >
              <h2 className="mb-2 text-xl font-bold text-gray-900 sm:text-2xl">{block.heading}</h2>

              <MarkdownMath className="text-[15px] font-medium leading-relaxed text-gray-800">
                {block.niveau1.content}
              </MarkdownMath>

              {block.niveau2.content.trim() && (
                <details
                  className="group mt-3 rounded-xl border border-gray-200 bg-white"
                  onToggle={(e) => {
                    if ((e.currentTarget as HTMLDetailsElement).open) fire(i, 'level2_open');
                  }}
                >
                  <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-2 px-4 py-2.5 text-sm font-semibold text-gray-800 [&::-webkit-details-marker]:hidden">
                    <span>{block.niveau2.title || BLOG_LEVEL_LABELS.niveau2}</span>
                    <ChevronDown
                      className="size-4 shrink-0 text-gray-400 transition-transform group-open:rotate-180"
                      aria-hidden
                    />
                  </summary>
                  <div className="px-4 pb-3">
                    <MarkdownMath className="text-[15px] leading-relaxed text-gray-700">
                      {block.niveau2.content}
                    </MarkdownMath>
                  </div>
                </details>
              )}

              {block.niveau3.content.trim() && (
                <details
                  className="group mt-2 rounded-xl border border-gray-200 bg-white"
                  onToggle={(e) => {
                    if ((e.currentTarget as HTMLDetailsElement).open) fire(i, 'level3_open');
                  }}
                >
                  <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-2 px-4 py-2.5 text-sm font-semibold text-gray-800 [&::-webkit-details-marker]:hidden">
                    <span>{block.niveau3.title || BLOG_LEVEL_LABELS.niveau3}</span>
                    <ChevronDown
                      className="size-4 shrink-0 text-gray-400 transition-transform group-open:rotate-180"
                      aria-hidden
                    />
                  </summary>
                  <div className="px-4 pb-3">
                    <MarkdownMath className="text-[15px] leading-relaxed text-gray-700">
                      {block.niveau3.content}
                    </MarkdownMath>
                  </div>
                </details>
              )}

              {block.poll &&
                (() => {
                  // Version « article complet » : sondage en lecture seule.
                  if (revealAll) {
                    return (
                      <BlogPoll
                        poll={block.poll}
                        answeredChoice={null}
                        counts={counts[i] ?? []}
                        revealCounts
                        readOnly
                        onVote={() => {}}
                        pending={false}
                      />
                    );
                  }

                  const recorded = votes[i] !== undefined;

                  // Toujours authentifié → sondage interactif dès qu'il est
                  // révélé ; les barres ne se dévoilent qu'une fois le vote
                  // ENREGISTRÉ.
                  return (
                    <div>
                      <BlogPoll
                        poll={block.poll}
                        answeredChoice={votes[i] ?? null}
                        counts={counts[i] ?? []}
                        revealCounts={recorded}
                        onVote={(ci) => handleVote(i, ci)}
                        pending={pendingBlocks.has(i)}
                      />
                      {voteError === i && (
                        <p className="mt-2 text-xs font-medium text-red-600">
                          Le vote n&apos;a pas pu être enregistré. Vérifie ta connexion et re-clique
                          sur ton choix.
                        </p>
                      )}
                      {!recorded && !isLast && i === activeGateIndex && (
                        <div className="mt-4 flex justify-center">
                          <button
                            type="button"
                            onClick={() => setSkipped((s) => ({ ...s, [i]: true }))}
                            className="border-level-400 bg-level-100 text-level-600 hover:bg-level-400 hover:text-ink-900 inline-flex min-h-11 items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-semibold transition active:scale-[0.98]"
                          >
                            Continuer la lecture sans voter
                            <ArrowRight className="size-4 shrink-0" aria-hidden />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })()}
            </section>
          </div>
        );
      })}

      {/* Conclusion — dévoilée quand tout le parcours est parcouru */}
      {conclusionMd.trim() && (
        <section
          className={cn(
            'transition-all',
            allRevealed
              ? 'mt-8 max-h-none opacity-100 duration-700'
              : 'pointer-events-none max-h-0 overflow-hidden opacity-0',
          )}
          aria-hidden={!allRevealed}
        >
          <div className="border-primary-100 bg-primary-50/40 rounded-2xl border p-5">
            <p className="text-primary-600 mb-2 text-[11px] font-bold uppercase tracking-wide">
              Pour continuer à penser
            </p>
            <MarkdownMath className="text-[15px] leading-relaxed text-gray-800">
              {conclusionMd}
            </MarkdownMath>
          </div>
        </section>
      )}

      {/* Récompense de fin — dévoilée une fois tout le parcours terminé */}
      <section
        className={cn(
          'transition-all',
          allRevealed
            ? 'mt-8 max-h-none opacity-100 duration-700'
            : 'pointer-events-none max-h-0 overflow-hidden opacity-0',
        )}
        aria-hidden={!allRevealed}
      >
        <BlogCompletionReward title={articleTitle} socialLinks={socialLinks} />
      </section>
    </div>
  );
}
